/**
 * Detail for the Fused Tracks source (ENG-342): what the platform's fused feed
 * is publishing right now and how the raw feeds behind it contribute. Opened
 * from the Fused Tracks card in the Surveillance list; while it is open the map
 * shows only the fused aircraft.
 */
import { InfoRow } from '@/components/InfoRow';
import { Section, SectionGroup } from '@/components/Section';
import { Badge } from '@/components/ui/badge';
import { feedColor } from '@/lib/map/layers/fusedSources';
import { FUSED_INPUT_FEEDS } from '@/lib/mqtt/legacy/fusedTracks';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { offsetMetres } from '@/lib/surv/fusedSummary';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFusedSummary } from './useFusedSummary';

/** Short names for the tight Selected Aircraft rows. */
const FEED_SHORT_NAME: Record<string, string> = {
  live_ads: 'ADS-B',
  live_mlat: 'MLAT',
  live_community: 'Community',
};

const FEED_NAME: Record<string, string> = {
  live_ads: 'ADS-B',
  live_mlat: 'MLAT',
  live_community: 'Community (ADS-B Exchange)',
};

/** The selected aircraft and which feeds reported it — the table in Brayden's
 *  viewer: feed, how long ago, and how far from the fused position. */
function SelectedAircraft() {
  const target = useTargetsStore((s) => (s.selectedId ? s.byId[s.selectedId] : undefined));
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <Section title="Selected Aircraft">
      {!target ? (
        <p className="text-[0.692em] text-(--color-text3) leading-snug">
          Click an aircraft on the map to see which feeds reported it. Its feeds are drawn as
          circles, each joined to the fused position by a dashed line.
        </p>
      ) : target.sources === undefined ? (
        <p className="text-[0.692em] text-(--color-text3) leading-snug">
          {target.callsign} is not a fused track.
        </p>
      ) : (
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
          <div className="text-[0.846em] font-bold font-mono mb-1">{target.callsign}</div>
          {target.sources.length === 0 ? (
            <p className="text-[0.692em] text-(--color-text3)">No feed has reported it recently.</p>
          ) : (
            target.sources.map((src) => (
              <InfoRow
                key={src.service_id}
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: feedColor(src.service_id) }}
                      aria-hidden
                    />
                    {FEED_SHORT_NAME[src.service_id] ?? src.service_id}
                  </span>
                }
                value={`${src.at != null ? `${Math.max(0, Math.round((now - src.at) / 1000))} s ago · ` : ''}${Math.round(offsetMetres(src, target))} m`}
                mono
              />
            ))
          )}
        </div>
      )}
    </Section>
  );
}

export function FusedTracksDetail() {
  const setMode = useSurvStore((s) => s.setMode);
  const sum = useFusedSummary();
  const seenBy = Object.entries(sum.seenBy).sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button
          type="button"
          onClick={() => setMode('fused')}
          className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80"
        >
          <ArrowLeft size={13} /> Surveillance
        </button>
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold">Fused Tracks</h2>
          <Badge variant={sum.live ? 'ok' : 'crit'}>{sum.live ? 'Active' : 'Offline'}</Badge>
        </div>
        <div className="text-[0.692em] text-(--color-text3) mt-0.5 font-mono">
          Platform fusion of ADS-B, MLAT and community feeds
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        <SectionGroup>
          <Section title="Fused Feed">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              <InfoRow
                label="Status"
                value={sum.live ? 'Active' : 'Offline'}
                valueClassName={
                  sum.live ? 'text-(--color-green) font-bold' : 'text-(--color-red) font-bold'
                }
              />
              <InfoRow label="Tracks" value={String(sum.tracks)} mono />
              <InfoRow label="Coasting" value={String(sum.coasting)} mono />
            </div>
            {!sum.live && (
              <p className="text-[0.692em] text-(--color-text3) mt-1.5 leading-snug">
                The fused feed isn't publishing right now, so the map is showing the raw feeds.
              </p>
            )}
          </Section>

          <SelectedAircraft />

          <Section title="Input Feeds">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              {FUSED_INPUT_FEEDS.map((feed) => (
                <InfoRow
                  key={feed}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: feedColor(feed) }}
                        aria-hidden
                      />
                      {FEED_NAME[feed] ?? feed}
                    </span>
                  }
                  value={`${sum.perFeed[feed] ?? 0} aircraft`}
                  mono
                />
              ))}
            </div>
            <p className="text-[0.692em] text-(--color-text3) mt-1.5 leading-snug">
              Select an aircraft to see where each of these feeds last put it.
            </p>
          </Section>

          {sum.live && seenBy.length > 0 && (
            <Section title="Tracks Seen By">
              <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
                {seenBy.map(([key, n]) => (
                  <InfoRow key={key} label={key} value={String(n)} mono />
                ))}
              </div>
            </Section>
          )}
        </SectionGroup>
      </div>
    </div>
  );
}
